import { useState, useRef, useEffect } from 'react';
import { Plus, X, Star, Loader2, Pencil, Check } from 'lucide-react';

interface MultiFieldItem {
  id: string;
  value: string;
  label: string;
  is_primary: boolean;
}

interface ContactMultiFieldEditorProps {
  items: MultiFieldItem[];
  fieldType: 'email' | 'phone' | 'account';
  labelOptions: { value: string; label: string }[];
  placeholder: string;
  onAdd: (value: string, label: string, isPrimary: boolean) => Promise<unknown>;
  onRemove: (id: string) => Promise<void>;
  onSetPrimary: (id: string) => Promise<void>;
  onUpdate?: (id: string, value: string, label: string) => Promise<void>;
  readOnly?: boolean;
}

export function ContactMultiFieldEditor({
  items,
  fieldType,
  labelOptions,
  placeholder,
  onAdd,
  onRemove,
  onSetPrimary,
  onUpdate,
  readOnly = false,
}: ContactMultiFieldEditorProps) {
  const [adding, setAdding] = useState(false);
  const [newValue, setNewValue] = useState('');
  const [newLabel, setNewLabel] = useState(labelOptions[0]?.value || '');
  const [saving, setSaving] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const [editLabel, setEditLabel] = useState('');
  const [editSaving, setEditSaving] = useState(false);
  const editInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editingId && editInputRef.current) {
      editInputRef.current.focus();
    }
  }, [editingId]);

  const handleAdd = async () => {
    if (!newValue.trim()) return;
    setSaving(true);
    const isPrimary = items.length === 0;
    await onAdd(newValue.trim(), newLabel, isPrimary);
    setNewValue('');
    setNewLabel(labelOptions[0]?.value || '');
    setAdding(false);
    setSaving(false);
  };

  const handleRemove = async (id: string) => {
    setRemovingId(id);
    await onRemove(id);
    setRemovingId(null);
  };

  const handleStartEdit = (item: MultiFieldItem) => {
    if (readOnly || !onUpdate) return;
    setEditingId(item.id);
    setEditValue(item.value);
    setEditLabel(item.label);
  };

  const handleSaveEdit = async () => {
    if (!editingId || !onUpdate || !editValue.trim()) return;
    const item = items.find(i => i.id === editingId);
    if (item && (editValue.trim() !== item.value || editLabel !== item.label)) {
      setEditSaving(true);
      await onUpdate(editingId, editValue.trim(), editLabel);
      setEditSaving(false);
    }
    setEditingId(null);
  };

  const handleEditKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSaveEdit();
    }
    if (e.key === 'Escape') {
      // Cancels this row only: the sheet around it stays open.
      e.preventDefault();
      setEditingId(null);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAdd();
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      setAdding(false);
      setNewValue('');
    }
  };

  const inputType = fieldType === 'email' ? 'email' : 'tel';

  return (
    <div className="space-y-2">
      {items.map((item) => (
        <div
          key={item.id}
          className="flex items-center gap-2 group"
        >
          {/* Primary indicator */}
          <button
            onClick={() => !item.is_primary && !readOnly && onSetPrimary(item.id)}
            disabled={item.is_primary || readOnly}
            className={`flex-shrink-0 p-1 rounded transition-colors ${
              item.is_primary
                ? 'text-amber-500'
                : 'text-desk-faint hover:text-amber-400'
            }`}
            title={item.is_primary ? 'Principal' : 'Definir comme principal'}
            aria-label={item.is_primary ? `${item.value}, principal` : `Faire de ${item.value} le principal`}
          >
            <Star className="w-3.5 h-3.5" fill={item.is_primary ? 'currentColor' : 'none'} />
          </button>

          {editingId === item.id ? (
            /* Inline edit mode */
            <>
              <input
                ref={editInputRef}
                type={inputType}
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                onKeyDown={handleEditKeyDown}
                className="flex-1 min-w-0 px-2 py-1 text-sm bg-desk-bg text-desk-ink border border-desk-focus rounded focus:ring-1 focus:ring-desk-focus/20 focus:border-desk-focus outline-none"
              />
              <select
                value={editLabel}
                onChange={(e) => setEditLabel(e.target.value)}
                className="px-2 py-1 text-xs border border-desk-ring rounded bg-desk-bg text-desk-ink"
              >
                {labelOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <button
                onClick={handleSaveEdit}
                disabled={editSaving || !editValue.trim()}
                className="flex-shrink-0 p-1 text-emerald-600 hover:text-emerald-700 disabled:text-desk-faint"
                title="Valider"
                aria-label="Valider"
              >
                {editSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
              </button>
              <button
                onClick={() => setEditingId(null)}
                className="flex-shrink-0 p-1 text-desk-muted hover:text-desk-ink"
                title="Annuler"
                aria-label="Annuler"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </>
          ) : (
            /* Display mode */
            <>
              {/* Value */}
              <span className="text-sm text-desk-ink flex-1 min-w-0 truncate">
                {fieldType === 'email' ? (
                  <a href={`mailto:${item.value}`} className="hover:text-desk-accent-text hover:underline">
                    {item.value}
                  </a>
                ) : fieldType === 'phone' ? (
                  <a href={`tel:${item.value}`} className="hover:text-desk-accent-text hover:underline">
                    {item.value}
                  </a>
                ) : (
                  item.value
                )}
              </span>

              {/* Label badge */}
              <span className="flex-shrink-0 px-2 py-0.5 text-xs rounded-full bg-desk-subtle text-desk-soft">
                {labelOptions.find(o => o.value === item.label)?.label || item.label}
              </span>

              {/* Edit button */}
              {!readOnly && onUpdate && (
                <button
                  onClick={() => handleStartEdit(item)}
                  className="flex-shrink-0 p-1 text-desk-faint hover:text-desk-accent-text opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 focus:opacity-100 transition-all"
                  title="Modifier"
                  aria-label={`Modifier ${item.value}`}
                >
                  <Pencil className="w-3.5 h-3.5" />
                </button>
              )}

              {/* Remove button */}
              {!readOnly && (
                <button
                  onClick={() => handleRemove(item.id)}
                  disabled={removingId === item.id}
                  className="flex-shrink-0 p-1 text-desk-faint hover:text-red-500 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 focus:opacity-100 transition-all"
                  title="Supprimer"
                  aria-label={`Supprimer ${item.value}`}
                >
                  {removingId === item.id ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <X className="w-3.5 h-3.5" />
                  )}
                </button>
              )}
            </>
          )}
        </div>
      ))}

      {/* Add form */}
      {!readOnly && (
        <>
          {adding ? (
            <div className="flex items-center gap-2">
              <input
                type={inputType}
                value={newValue}
                onChange={(e) => setNewValue(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={placeholder}
                className="flex-1 min-w-0 px-2 py-1 text-sm bg-desk-bg text-desk-ink border border-desk-ring rounded focus:ring-1 focus:ring-desk-focus/20 focus:border-desk-focus outline-none"
                autoFocus
              />
              <select
                value={newLabel}
                onChange={(e) => setNewLabel(e.target.value)}
                className="px-2 py-1 text-xs border border-desk-ring rounded bg-desk-bg text-desk-ink"
              >
                {labelOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <button
                onClick={handleAdd}
                aria-label="Ajouter"
                disabled={saving || !newValue.trim()}
                className="p-1 text-desk-accent-text hover:brightness-90 disabled:text-desk-faint"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              </button>
              <button
                onClick={() => { setAdding(false); setNewValue(''); }}
                aria-label="Annuler l'ajout"
                className="p-1 text-desk-muted hover:text-desk-ink"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <button
              onClick={() => setAdding(true)}
              className="flex items-center gap-1 text-xs text-desk-accent-text hover:brightness-90 py-1"
            >
              <Plus className="w-3.5 h-3.5" />
              Ajouter
            </button>
          )}
        </>
      )}
    </div>
  );
}

// Preset label options
export const EMAIL_LABELS = [
  { value: 'pro', label: 'Pro' },
  { value: 'perso', label: 'Perso' },
  { value: 'autre', label: 'Autre' },
];

export const PHONE_LABELS = [
  { value: 'mobile', label: 'Mobile' },
  { value: 'fixe', label: 'Fixe' },
  { value: 'pro', label: 'Pro' },
  { value: 'perso', label: 'Perso' },
];
